import { LoadingButton } from "@mui/lab";
import { Stack, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import NumericField from "../common/NumericField/NumericField";
import { SettingsDetailsType } from "../../types/genericType";
import { updateSettingsDetails } from "../../services/settingsService";
import CustomSnackbar from "../common/Snackbar/CustomSnackbar";

type SettingPanelPropTypes = {
  settingsData: SettingsDetailsType;
};

const SettingsPanel: React.FC<SettingPanelPropTypes> = (props) => {
  const { settingsData } = props;
  const [onSuccess, setOnSuccess] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [LoanMessage, setLoanMessage] = useState(
    settingsData?.LoanMessage ?? ""
  );
  const [InvestmentMessage, setInvestmentMessage] = useState(
    settingsData?.InvestmentMessage ?? ""
  );
  const [Quote, setQuote] = useState(settingsData?.Quote ?? "");
  const [StatementEmailSubject, setStatementEmailSubject] = useState(
    settingsData?.StatementEmailSubject ?? ""
  );
  const [StatementEmailBody, setStatementEmailBody] = useState(
    settingsData?.StatementEmailBody ?? ""
  );
  const [MonthEndDescription, setMonthEndDescription] = useState(
    settingsData?.MonthEndDescription ?? ""
  );
  const [WelcomeBanner, setWelcomeBanner] = useState(
    settingsData?.WelcomeBanner ?? ""
  );

  const [ManagementRate, setManagementRate] = useState(
    settingsData?.ManagementRate ?? ""
  );
  const [InvestmentRate, setInvestmentRate] = useState(
    settingsData?.VariableInvestmentRate ?? ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});

  const saveChanges = async () => {
    setIsLoading(true);
    const updateField = async (field: string, value: any) => {
      try {
        await updateSettingsDetails(field, value);
      } catch (error) {
        console.error(`Error updating ${field}`, error);
      }
    };
    for (const [field, value] of Object.entries(updatedFields)) {
      await updateField(field, value);
    }
    setIsLoading(false);
    setOnSuccess(true);
    setSnackbarMsg("Details Updated Successfully");
    setUpdatedFields({});
  };

  useEffect(() => {
    setLoanMessage(settingsData?.LoanMessage ?? "");
    setInvestmentMessage(settingsData?.InvestmentMessage ?? "");
    setQuote(settingsData?.Quote ?? "");
    setStatementEmailSubject(settingsData?.StatementEmailSubject ?? "");
    setStatementEmailBody(settingsData?.StatementEmailBody ?? "");
    setMonthEndDescription(settingsData?.MonthEndDescription ?? "");
    setWelcomeBanner(settingsData?.WelcomeBanner ?? "");
    setManagementRate(settingsData?.ManagementRate ?? "");
    setInvestmentRate(settingsData?.VariableInvestmentRate ?? "");
  }, [settingsData]);

  return (
    <Stack spacing={3}>
      <TextField
        id="outlined-helperText"
        label="Loan Message"
        size="small"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        value={LoanMessage}
        onChange={(event) => {
          setLoanMessage(event.target.value as string);
          setUpdatedFields({
            ...updatedFields,
            LoanMessage: event.target.value,
          });
        }}
      />
      <TextField
        id="outlined-helperText"
        label="Investment Message"
        size="small"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        value={InvestmentMessage}
        onChange={(event) => {
          setInvestmentMessage(event.target.value as string);
          setUpdatedFields({
            ...updatedFields,
            InvestmentMessage: event.target.value,
          });
        }}
      />
      <TextField
        id="outlined-helperText"
        label="Quote"
        size="small"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        value={Quote}
        onChange={(event) => {
          setQuote(event.target.value as string);
          setUpdatedFields({ ...updatedFields, Quote: event.target.value });
        }}
      />
      <TextField
        id="outlined-helperText"
        label="Statement Email Subject"
        size="small"
        fullWidth
        value={StatementEmailSubject}
        onChange={(event) => {
          setStatementEmailSubject(event.target.value as string);
          setUpdatedFields({
            ...updatedFields,
            StatementEmailSubject: event.target.value,
          });
        }}
      />
      <TextField
        id="outlined-helperText"
        label="Statement Email Body"
        size="small"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        value={StatementEmailBody}
        onChange={(event) => {
          setStatementEmailBody(event.target.value as string);
          setUpdatedFields({
            ...updatedFields,
            StatementEmailBody: event.target.value,
          });
        }}
      />
      <Stack spacing={2} direction="row">
        <NumericField
          label="Mgmt Rate"
          val={ManagementRate}
          setVal={setManagementRate}
          testId="percent-owned"
          decimalPlaces={5}
          placeholder="0.00%"
          endsWith="%"
          callback={(val: number) =>
            setUpdatedFields({
              ...updatedFields,
              ManagementRate: val,
            })
          }
        />
        <NumericField
          label="Var Inv Rate"
          val={InvestmentRate}
          setVal={setInvestmentRate}
          testId="percent-owned"
          decimalPlaces={5}
          placeholder="0.00%"
          endsWith="%"
          callback={(val: number) =>
            setUpdatedFields({
              ...updatedFields,
              VariableInvestmentRate: val,
            })
          }
        />
      </Stack>
      <TextField
        id="outlined-helperText"
        label="Month End Description"
        size="small"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        value={MonthEndDescription}
        onChange={(event) => {
          setMonthEndDescription(event.target.value as string);
          setUpdatedFields({
            ...updatedFields,
            MonthEndDescription: event.target.value,
          });
        }}
      />
      <TextField
        id="outlined-helperText"
        label="Welcome Banner"
        size="small"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        value={WelcomeBanner}
        onChange={(event) => {
          setWelcomeBanner(event.target.value as string);
          setUpdatedFields({
            ...updatedFields,
            WelcomeBanner: event.target.value,
          });
        }}
      />
      <LoadingButton
        variant="contained"
        data-testid="createTxnBtn"
        size="small"
        sx={{
          height: "36px",
          width: "186px",
          color: "#FFFFFF",
        }}
        onClick={saveChanges}
        loading={isLoading}
      >
        SAVE CHANGES
      </LoadingButton>
      <CustomSnackbar
        open={onSuccess}
        message={snackbarMsg}
        setOpen={setOnSuccess}
      />
    </Stack>
  );
};

export default SettingsPanel;
